// src/pages/faq/FAQPage.tsx
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useViewport } from "../../common/hooks/ViewPort";
import { SubConHeader } from "src/components/atoms/SubHeaderBox/SubConHeader";

type FAQ = {
  id: number;
  category: "분류1" | "분류2";
  q: string;
  a: string;
};

const CATEGORIES = ["전체", "분류1", "분류2"] as const;

const FAQPage: React.FC = () => {
  const navi = useNavigate();
  const { px } = useViewport();

  //더미 데이터
  const data: FAQ[] = useMemo(
    () => [
      {
        id: 1,
        category: "분류1",
        q: "질문 내용",
        a: "신규 회원가입을 통해 서비스 참여가 가능합니다.",
      },
      {
        id: 2,
        category: "분류2",
        q: "질문 내용",
        a: "앱 설정에서 알림을 켤 수 있어요.",
      },
      {
        id: 3,
        category: "분류1",
        q: "질문 내용",
        a: "비밀번호는 마이페이지에서 변경할 수 있습니다.",
      },
    ],
    []
  );

  const [active, setActive] = useState<(typeof CATEGORIES)[number]>("전체");
  const [openIds, setOpenIds] = useState<Set<number>>(new Set([1])); // 첫 항목 오픈

  const list = useMemo(
    () =>
      active === "전체" ? data : data.filter((d) => d.category === active),
    [active, data]
  );

  const toggle = (id: number) =>
    setOpenIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  return (
    <div
      style={{
        width: "100vw",
        height: "100vh",
        maxHeight: "100vh",
        maxWidth: "45rem",
        margin: "0 auto",
        position: "relative",
        background: "#F7F7F7",
        padding: "0 16px",
        boxSizing: "border-box",
        overflowY: "auto",
      }}
    >
      <div
        style={{
          width: "100%",
          height: "100vh",
          maxHeight: "100vh",
          position: "relative",
          background: "#F7F7F7",
          boxSizing: "border-box",
        }}
      >
        {/* 헤더 */}
        <SubConHeader title="자주 묻는 질문" onClickArrow={() => navi(-1)} />

        {/* 탭 */}
        <div style={{ marginTop: px(4), position: "relative" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${CATEGORIES.length}, 1fr)`,
              borderBottom: `${px(1)}px solid #E5E7EB`,
            }}
          >
            {CATEGORIES.map((c) => {
              const isActive = c === active;
              return (
                <button
                  key={c}
                  onClick={() => setActive(c)}
                  style={{
                    position: "relative",
                    padding: `${px(12)}px 0`,
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "center",
                    fontSize: px(13),
                    fontWeight: 600,
                    color: isActive ? "#111827" : "#6B7280",
                  }}
                >
                  {c}
                  {isActive && (
                    <span
                      style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: -px(1),
                        height: px(3),
                        background: "#F97316",
                        borderRadius: px(3),
                      }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* 리스트 */}
        <div style={{ marginTop: px(12), display: "grid", gap: px(12) }}>
          {list.map((item) => {
            const opened = openIds.has(item.id);
            return (
              <section
                key={item.id}
                style={{
                  background: "#FFFFFF",
                  borderRadius: px(14),
                  boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                  overflow: "hidden",
                }}
              >
                {/* 질문 행 */}
                <button
                  onClick={() => toggle(item.id)}
                  style={{
                    width: "100%",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    padding: `${px(14)}px`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: px(8),
                    }}
                  >
                    <span
                      style={{
                        color: "#9CA3AF",
                        fontWeight: 700,
                        fontSize: px(14),
                      }}
                    >
                      Q.
                    </span>
                    <span
                      style={{
                        color: "#111827",
                        fontWeight: 700,
                        fontSize: px(14),
                      }}
                    >
                      {item.q}
                    </span>
                  </div>
                  <svg
                    width={px(18)}
                    height={px(18)}
                    viewBox="0 0 24 24"
                    style={{
                      transform: `rotate(${opened ? 180 : 0}deg)`,
                      transition: "transform .18s ease",
                    }}
                  >
                    <path
                      d="M6 9l6 6 6-6"
                      stroke="#9CA3AF"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                  </svg>
                </button>

                {/* 구분선 */}
                {opened && (
                  <div
                    style={{
                      height: 1,
                      background: "#F1F2F4",
                      margin: `0 ${px(14)}px`,
                    }}
                  />
                )}

                {/* 답변 영역 */}
                {opened && (
                  <div
                    style={{ padding: `${px(12)}px ${px(14)}px ${px(14)}px` }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: px(8),
                      }}
                    >
                      <span
                        style={{
                          color: "#F97316",
                          fontWeight: 800,
                          fontSize: px(14),
                        }}
                      >
                        A.
                      </span>
                      <span
                        style={{
                          color: "#6B7280",
                          fontSize: px(13),
                          lineHeight: 1.6,
                        }}
                      >
                        {item.a}
                      </span>
                    </div>
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
